/**
 * useMessageSeenDwell — "you have READ this message" is a fact about
 * attention on THAT message (2026-09-10; Miles 2026-09-09: "the identifier
 * that shows whom has viewed a message needs to be on all messages … if
 * they've had open to that particular message for more than 2 seconds";
 * clarified: only messages actually lingered on).
 *
 * One IntersectionObserver rooted at the transcript scroller watches every
 * message row (`[data-reaction-row]`, resolved to its message through the
 * host's row map). A row counts as "on screen" while the tab is visible AND
 * at least half of it is in view — or, for a bubble taller than the
 * scroller, at least 60 % of the scroller's height is covered by it. A timer
 * per row arms on entry and clears on exit; at MESSAGE_SEEN_DWELL_MS the
 * row's shared anchor + instant are handed to `onSeen` exactly once per
 * (thread, anchor). The tab hiding clears every timer; the tab coming back
 * rebuilds the observer (a fresh observer re-emits the current entries, so
 * a row still on screen starts a fresh dwell). A thread switch or a disable
 * tears everything down.
 *
 * Never observed: system rows, rows without a shared anchor (a legacy bubble
 * the store cannot key), the viewer's OWN messages (their receipt is never
 * rendered), and anchors already marked (`alreadySeen`).
 */
import { useEffect, useRef, type RefObject } from 'react';
import type { ChatMessage } from './types';
import { messageAuthorId, messageOrderTs, type SeenMark } from '../../services/groupThreads/seenBy';

export const MESSAGE_SEEN_DWELL_MS = 2_000;
/** Half the row in view counts … */
export const MESSAGE_SEEN_MIN_RATIO = 0.5;
/** … or, for a tall bubble, this much of the scroller covered by it. */
export const MESSAGE_SEEN_MIN_ROOT_COVERAGE = 0.6;

export interface UseMessageSeenDwellArgs {
  enabled: boolean;
  threadId: string | null | undefined;
  /** The transcript scroller (the observer's root). */
  scrollerRef: RefObject<HTMLElement | null>;
  /** The host's row map: `data-reaction-row` value → the message rendered there. */
  rowsRef: RefObject<Map<string, ChatMessage>>;
  selfUid: string | null | undefined;
  /** Anchors the viewer's stored receipt already carries — never re-observed. */
  alreadySeen?: Readonly<Record<string, number>> | null;
  onSeen: (threadId: string, marks: SeenMark[]) => void;
  /** Test seam; production uses MESSAGE_SEEN_DWELL_MS. */
  dwellMs?: number;
  /** Bumped by the host when the row set may have changed (a new message, a reveal). */
  rowsVersion?: number;
}

function tabVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

/** The visibility rule, pure: half the row, or 60 % of the root covered. */
export function rowSufficientlyVisible(entry: Pick<IntersectionObserverEntry, 'isIntersecting' | 'intersectionRatio' | 'intersectionRect' | 'rootBounds'>): boolean {
  if (!entry.isIntersecting) return false;
  if (entry.intersectionRatio >= MESSAGE_SEEN_MIN_RATIO) return true;
  const rootHeight = entry.rootBounds?.height ?? 0;
  const shown = entry.intersectionRect?.height ?? 0;
  return rootHeight > 0 && shown >= MESSAGE_SEEN_MIN_ROOT_COVERAGE * rootHeight;
}

export function useMessageSeenDwell(args: UseMessageSeenDwellArgs): void {
  const { enabled, threadId, scrollerRef, rowsRef, selfUid, alreadySeen, dwellMs, rowsVersion } = args;
  const onSeenRef = useRef(args.onSeen);
  onSeenRef.current = args.onSeen;
  const alreadyRef = useRef(alreadySeen);
  alreadyRef.current = alreadySeen;
  const selfRef = useRef(selfUid);
  selfRef.current = selfUid;
  /** Anchors fired this session, per thread. */
  const firedRef = useRef<Map<string, Set<string>>>(new Map());
  /** The live observer's "observe any new rows" step, for the rowsVersion effect below. */
  const observeRowsRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const id = (threadId ?? '').trim();
    if (!enabled || !id) return undefined;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return undefined;
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    const ms = typeof dwellMs === 'number' && dwellMs >= 0 ? dwellMs : MESSAGE_SEEN_DWELL_MS;
    const timers = new Map<Element, ReturnType<typeof setTimeout>>();
    const observed = new Set<Element>();
    let observer: IntersectionObserver | null = null;

    const fired = (() => {
      let set = firedRef.current.get(id);
      if (!set) {
        set = new Set<string>();
        firedRef.current.set(id, set);
      }
      return set;
    })();

    const anchorOf = (el: Element): { anchor: string; message: ChatMessage } | null => {
      const anchor = (el.getAttribute('data-shared-message-id') ?? '').trim();
      if (!anchor) return null;
      const rowKey = el.getAttribute('data-reaction-row') ?? '';
      const message = rowsRef.current?.get(rowKey);
      if (!message || message.isSystem) return null;
      const self = (selfRef.current ?? '').trim();
      if (self && messageAuthorId(message) === self) return null;
      if (fired.has(anchor)) return null;
      if (alreadyRef.current && Object.prototype.hasOwnProperty.call(alreadyRef.current, anchor)) return null;
      return { anchor, message };
    };

    const disarm = (el: Element): void => {
      const t = timers.get(el);
      if (t !== undefined) {
        clearTimeout(t);
        timers.delete(el);
      }
    };
    const disarmAll = (): void => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
    const arm = (el: Element): void => {
      disarm(el);
      if (!tabVisible()) return;
      timers.set(el, setTimeout(() => {
        timers.delete(el);
        const hit = anchorOf(el);
        if (!hit) return;
        const orderTs = messageOrderTs(hit.message);
        if (orderTs === null) return;
        fired.add(hit.anchor);
        observer?.unobserve(el);
        observed.delete(el);
        try {
          onSeenRef.current(id, [{ id: hit.anchor, orderTs }]);
        } catch {
          /* a failing receiver never breaks the view */
        }
      }, ms));
    };

    const onEntries = (entries: IntersectionObserverEntry[]): void => {
      for (const entry of entries) {
        if (rowSufficientlyVisible(entry)) {
          if (!timers.has(entry.target)) arm(entry.target);
        } else {
          disarm(entry.target);
        }
      }
    };

    const observeRows = (): void => {
      if (!observer) return;
      const rows = scroller.querySelectorAll('[data-reaction-row]');
      rows.forEach((el) => {
        if (observed.has(el)) return;
        if (!anchorOf(el)) return;
        observed.add(el);
        observer!.observe(el);
      });
    };

    const build = (): void => {
      observer = new IntersectionObserver(onEntries, {
        root: scroller,
        threshold: [0, MESSAGE_SEEN_MIN_RATIO, 1],
      });
      observed.clear();
      observeRows();
    };
    const teardown = (): void => {
      disarmAll();
      observer?.disconnect();
      observer = null;
      observed.clear();
    };
    const onVisibility = (): void => {
      if (tabVisible()) {
        // A fresh observer re-emits the current entries: a row still on
        // screen starts a fresh dwell from now, not from before the tab hid.
        teardown();
        build();
      } else {
        disarmAll();
      }
    };

    build();
    observeRowsRef.current = observeRows;
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    return () => {
      observeRowsRef.current = null;
      teardown();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, threadId, scrollerRef, rowsRef, dwellMs]);

  // A new row (a message that just landed, older history revealed) joins the
  // observer WITHOUT restarting the dwells already running on the rows that
  // were there — a busy thread must not keep resetting the clock.
  useEffect(() => {
    observeRowsRef.current?.();
  }, [rowsVersion]);
}
